import React, {useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import LinearProgress from '@mui/material/LinearProgress';
import CheckIcon from '@mui/icons-material/Check';
import {Modal} from '@mui/material';
import Grow from '@mui/material/Grow';
import background_image from '../../../../assets/images/onboarding/checklist_background.png';

interface ChecklistStep {
  label: string;
}

interface AppTourChecklistProps {
  open: boolean;
  progress: number;
  steps: ChecklistStep[];
  onClose: () => void;
}
const TITLE = [
  'Basics First!',
  'Build Your Profile!',
  'Check out the Data Hub!',
  "You're All Set to go!",
]
const AppTourChecklist: React.FC<AppTourChecklistProps> = ({open, progress, steps, onClose}) => {
  const [animatedProgress, setAnimatedProgress] = useState(Math.max(0, progress - 25));

  useEffect(() => {
    if (animatedProgress !== progress) {
      const timeout = setTimeout(() => setAnimatedProgress(progress), 1500);
      return () => clearTimeout(timeout);
    }
  }, [progress, animatedProgress]);
  
  const title = TITLE[Math.floor(progress / 25)-1];
  const completedStepsCount = Math.floor((progress / 100) * steps.length);

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        onClose();
      }}
      disableEscapeKeyDown
      disableAutoFocus
      disableEnforceFocus
      keepMounted
      aria-labelledby="checklist-title"
      aria-describedby="checklist-description"
      style={{zIndex: 99999}}
    >
      <Box
        sx={{
          position: 'fixed',
          bottom: 25,
          right: 25,
          width: '370px',
          height: '390px',
          borderRadius: 2.5,
          boxShadow: 6,
          zIndex: 2300,
          backgroundImage: `url(${background_image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography id="checklist-title" variant="inherit"
           sx={{
            fontSize: '22px', 
            fontWeight: 700, 
            color: '#fff',
            pl:8,
            pr:6,
            pt:8
          }}>
            {title}
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{color: '#D1B3E0', ml: 'auto', pr:4}}>
            <CloseIcon/>
          </IconButton>
        </Box>

        <Typography 
        id="checklist-description" 
        variant="inherit"
        sx={{
          fontSize: '16px', 
          fontWeight: 500, 
          color: '#fff',
          mb: 2,
          px:8
        }}>
          Here is your Onboarding Checklist
        </Typography>

        <Box mb={7} px={8} display="flex" alignItems="center" gap={6}>
          <Typography variant="inherit" sx={{fontSize: '16px', fontWeight: 500, color: '#fff'}}>
            {progress}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={animatedProgress}
            sx={{
              flex: 1,
              height: 23,
              transition: 'all 1ms ease',
              borderRadius: 1.2,
              background: '#BCBBE0',
              '& .MuiLinearProgress-bar': {
                background: '#9035DE',
              },
            }}
          />
        </Box>

        <Box px={8}>
         
          {/* Steps */}
          {steps.map((step, idx) => {
            const isCompleted = idx < completedStepsCount;
            const isNotLastStep = idx < steps.length - 1;
            return (
              <Box key={step.label} display="flex" alignItems="center" position="relative" mb={8} sx={{width: '100%'}}>
                <Box
                  sx={{
                    width: isCompleted ? 22 : 20,
                    height: isCompleted ? 22 : 20,
                    borderRadius: '50%',
                    border: isCompleted ? 'none' : '2px solid #fff',
                    background: isCompleted ? '#9035DE' : 'transparent',
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                  }}
                >
                  <Grow in={isCompleted} timeout={1800}>
                    <CheckIcon sx={{color: '#fff', fontSize: 16}}/>
                  </Grow>
                </Box>

                <Typography variant="inherit" sx={{fontSize: '14px', width: '100%', fontWeight: 500, color: '#fff'}}>
                  {step.label}
                </Typography>

                {isNotLastStep && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 9,
                      top: 19,
                      width: '2px',
                      height: '36px',
                      backgroundColor: '#9035DE',
                      zIndex: 0,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Modal>
  );
};

export default React.memo(AppTourChecklist);
